import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { styled } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { getProject, getProjectSettings } from '../../apis/ProjectApi';
import { who } from '../../apis/UserApi';
import { useAppSelector } from '../../hooks';
import {
  fetchResource,
  setCurrenProject,
  setCurrenProjectSetting,
} from '../../slices/projectSlice';

import ProjectSidebar from '../../components/ProjectSidebar';

import { getElevation } from '../../utils/Map3dUtil';

import SuspendDialog from './SuspendDialog';

const DashboardContainer = styled('div')`
  display: flex;
  color: white;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(180deg, #3e3e3e 0%, #212121 100%);
  & > div {
    margin-left: 60px;
  }
`;

const HomePage = () => {
  const dispatch = useDispatch();
  const selectedProjectId = useAppSelector(
    (store) => store.system.selectedProjectId,
  );
  const orgMap = useAppSelector((store) => store.system.orgMap);

  const { data: project, isPending: isProjectLoading } = useQuery({
    queryKey: ['get-project', selectedProjectId],
    queryFn: async () => {
      const res = await getProject(selectedProjectId as string);
      return res;
    },
    enabled: !!selectedProjectId,
    refetchOnWindowFocus: false,
  });

  useQuery({
    queryKey: ['get-project-setting', selectedProjectId],
    queryFn: async () => {
      const res = await getProjectSettings(selectedProjectId as string);
      if (res.data.data) {
        dispatch(setCurrenProjectSetting(res.data.data));
        return res.data.data;
      }
      return {
        id: '',
        projectId: '',
        userLimit: {
          admin: 0,
          viewer: 0,
          supervisor: 0,
        },
        areaLimit: 0,
        whitelistDomains: [],
        overviewType: 'normal',
        createdAt: '',
        updatedAt: '',
      };
    },
    enabled: !!selectedProjectId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (project) {
      const updateProjectWithElevation = async () => {
        let elevation: number = 0;
        if (project.altitude && project.altitude !== null) {
          elevation = project.altitude;
        } else {
          try {
            elevation = await getElevation(
              project.center[0],
              project.center[1],
            );
          } catch (error) {
            console.error('ampleTerrain error:', error);
            dispatch(setCurrenProject({ ...project, elevation: 0 }));
          }
        }

        dispatch(
          setCurrenProject({
            ...project,
            elevation: project.altitude ?? elevation,
          }),
        );
      };
      updateProjectWithElevation();
    }
  }, [project]);

  useQuery({
    queryKey: ['get-all-workers', selectedProjectId],
    queryFn: async () => {
      if (selectedProjectId) {
        dispatch(
          // @ts-ignore
          fetchResource({
            projectId: selectedProjectId,
            resourceType: 'worker',
          }),
        );
      }
      return null;
    },
    enabled: !!selectedProjectId,
    refetchOnWindowFocus: false,
    refetchInterval: 600000,
  });

  useQuery({
    queryKey: ['get-all-environments', selectedProjectId],
    queryFn: async () => {
      if (selectedProjectId) {
        dispatch(
          // @ts-ignore
          fetchResource({
            projectId: selectedProjectId,
            resourceType: 'environment',
          }),
        );
      }
      return null;
    },
    enabled: !!selectedProjectId,
    refetchOnWindowFocus: false,
    refetchInterval: 600000,
  });

  const status = orgMap[project?.orgId ?? '']?.status ?? 'ACTIVE';

  const { data: owner } = useQuery({
    queryKey: ['get-who', `ownerId=${project?.ownerId}`],
    queryFn: async () => {
      const res = await who({ id: project?.ownerId });
      return res.data.data;
    },
    enabled: !!project?.ownerId,
    refetchOnWindowFocus: false,
  });

  return (
    <DashboardContainer className="DashboardContainer">
      {!isProjectLoading && (
        <>
          {status === 'ACTIVE' && (
            <>
              <ProjectSidebar />
              <Outlet />
            </>
          )}
          <SuspendDialog open={status === 'SUSPENDED'} owner={owner} />
        </>
      )}
    </DashboardContainer>
  );
};

export default HomePage;
